import React, { Component, ReactNode } from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "./button";
import { captureException } from "@/lib/sentry";
import { logError } from "@/lib/logger";

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Static fallback UI, or function that receives the caught error and optional componentStack (for debugging React.Children.only etc). */
  fallback?: ReactNode | ((error: Error | null, errorInfo?: React.ErrorInfo | null) => ReactNode);
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo });
    captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
      tags: { source: "error_boundary" },
    });
    logError("ErrorBoundary caught an error", error);
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const fb = this.props.fallback;
        return typeof fb === "function" ? fb(this.state.error, this.state.errorInfo) : fb;
      }

      // STAB-010: Provide actionable error messages based on error type
      const errorMessage = this.state.error?.message ?? "";
      const errorMessageLower = errorMessage.toLowerCase();
      const isNetworkError = errorMessageLower.includes("network") || errorMessageLower.includes("fetch") || errorMessageLower.includes("connection");
      const isAuthError = errorMessageLower.includes("auth") || errorMessageLower.includes("login") || errorMessageLower.includes("session");
      const isTimeoutError = errorMessageLower.includes("timeout") || errorMessageLower.includes("timed out");
      const isColumnError = errorMessageLower.includes("column") && (errorMessageLower.includes("does not exist") || errorMessageLower.includes("saknas"));
      const isChildrenOnlyError = errorMessageLower.includes("children.only") && errorMessageLower.includes("single react element");
      // BUG-001 / Error translation: known ReferenceErrors → user-friendly message (no raw stack as primary copy)
      const isRefErrorLayoutEffect = errorMessageLower.includes("uselayouteffect") && (errorMessageLower.includes("is not defined") || errorMessageLower.includes("undefined"));
      const isRefErrorDashboardSteps = errorMessageLower.includes("dashboard_onboarding_steps") && errorMessageLower.includes("is not defined");
      const isRefErrorGeneric = this.state.error?.name === "ReferenceError" || errorMessageLower.includes("is not defined");
      
      // User-friendly title and description based on error type
      let title = "Något gick fel";
      let description = "Ett oväntat fel inträffade.";
      let actionTip = "";
      
      if (isRefErrorLayoutEffect || isRefErrorDashboardSteps) {
        title = "Sidan laddade inte klart";
        description = "Ett tekniskt fel hindrade visningen. Detta brukar lösas genom att ladda om.";
        actionTip = "Klicka på \"Ladda om sidan\" nedan. Om felet kvarstår, försök i en annan webbläsare.";
      } else if (isRefErrorGeneric && !isNetworkError && !isAuthError) {
        title = "Sidan kunde inte laddas";
        description = "Ett tekniskt fel uppstod. Ladda om sidan för att försöka igen.";
        actionTip = "Försök igen eller ladda om sidan.";
      } else if (isNetworkError) {
        title = "Anslutningsproblem";
        description = "Kunde inte ansluta till servern.";
        actionTip = "Kontrollera din internetanslutning och försök igen.";
      } else if (isAuthError) {
        title = "Inloggning krävs";
        description = "Din session har gått ut.";
        actionTip = "Ladda om sidan för att logga in igen.";
      } else if (isTimeoutError) {
        title = "Servern svarar inte";
        description = "Begäran tog för lång tid.";
        actionTip = "Vänta en stund och försök igen.";
      } else if (isColumnError) {
        title = "Uppdatering pågår";
        description = "Systemet uppdateras. Försök igen om en minut.";
        actionTip = "Om felet kvarstår, ladda om sidan.";
      } else if (isChildrenOnlyError) {
        title = "Ett fel uppstod";
        description = "En del av sidan kunde inte visas. Ladda om sidan för att försöka igen.";
        actionTip = "Klicka på \"Ladda om sidan\" nedan.";
      } else {
        actionTip = "Försök igen eller ladda om sidan.";
      }

      return (
        <div role="alert" aria-live="assertive" className="flex flex-col items-center justify-center p-4 sm:p-6 text-center space-y-4 min-h-[var(--empty-state-min-height-md)] sm:min-h-[var(--empty-state-min-height-lg)]">
          <AlertCircle className="h-8 w-8 sm:h-10 sm:w-10 text-destructive" aria-hidden="true" />
          <div className="space-y-2 max-w-md">
            <h3 className="text-base sm:text-lg font-medium text-foreground">{title}</h3>
            <p className="text-sm sm:text-base text-muted-foreground px-4">
              {description}
            </p>
            {actionTip && (
              <p className="text-xs sm:text-sm text-muted-foreground/80 px-4 italic">
                💡 {actionTip}
              </p>
            )}
            {/* Always show collapsible error details so user/support can see what failed (DEV: open by default) */}
            {this.state.error && (
              <details className="mt-4 text-left text-xs" open={import.meta.env.DEV}>
                <summary className="cursor-pointer text-muted-foreground font-medium">Visa felinformation</summary>
                <pre className="mt-2 p-2 bg-muted rounded overflow-auto max-h-48 text-foreground break-words whitespace-pre-wrap">
                  {this.state.error.message}
                  {this.state.error.stack && `\n\n${this.state.error.stack}`}
                  {this.state.errorInfo?.componentStack && `\n\nComponent stack:\n${this.state.errorInfo.componentStack}`}
                </pre>
              </details>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={this.handleReset}
              className="gap-2 text-sm sm:text-base min-h-[var(--touch-target)]"
              aria-label="Försök igen"
            >
              <RefreshCw className="h-4 w-4 shrink-0" aria-hidden />
              Försök igen
            </Button>
            <Button
              type="button"
              variant="link"
              className="text-sm text-muted-foreground hover:text-foreground underline"
              onClick={() => window.location.reload()}
              aria-label="Ladda om sidan"
            >
              Ladda om sidan
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Simple fallback for dialogs - just closes gracefully
export const DialogErrorFallback = ({ onClose, dataTestId }: { onClose?: () => void; dataTestId?: string }) => (
  <div className="flex flex-col items-center justify-center p-4 sm:p-6 text-center space-y-4 min-h-[var(--empty-state-min-height)] sm:min-h-[var(--empty-state-min-height-md)]" data-testid={dataTestId} role="alert">
    <AlertCircle className="h-8 w-8 sm:h-10 sm:w-10 text-destructive shrink-0" aria-hidden />
    <div className="space-y-2 max-w-md">
      <h3 className="text-base sm:text-lg font-medium text-foreground">Kunde inte ladda</h3>
      <p className="text-sm sm:text-base text-muted-foreground px-4">
        Något gick fel. Stäng och försök igen.
      </p>
    </div>
    {onClose && (
      <Button type="button" variant="outline" size="sm" onClick={onClose} className="text-sm sm:text-base min-h-[var(--touch-target)]" aria-label="Stäng">
        Stäng
      </Button>
    )}
  </div>
);
